/** Hyperliquid trade history CSV import (`broker_name: hyperliquid`). */

export const HYPERLIQUID_BROKER_SLUG = "hyperliquid";

/** Hyperliquid imports are crypto-only — use for execution-candles MD routing. */
export const HYPERLIQUID_MARKET_DATA_ASSET_CLASS = "crypto";

export function isHyperliquidBroker(brokerName: string | null | undefined): boolean {
  return String(brokerName || "").toLowerCase() === HYPERLIQUID_BROKER_SLUG;
}

/** Hidden on CSV import — route is fixed to Hyperliquid. */
export function isHyperliquidHiddenCsvField(field: string): boolean {
  return field.toLowerCase() === "broker_name";
}

/** Connect card only asks for account_name; routing fields stay internal. */
export function isHyperliquidHiddenCredentialField(field: string): boolean {
  const f = field.toLowerCase();
  return (
    f === "broker_name" ||
    f === "traded_asset_classes" ||
    f === "tradedassetclasses" ||
    f === "time_zone" ||
    f === "timezone"
  );
}

/** User-facing CSV field labels — overrides technical API `field_labels`. */
export const HYPERLIQUID_CSV_FIELD_LABELS: Record<string, string> = {
  account_name: "Account name",
  time_zone: "Trade history timezone",
  traded_asset_classes: "Asset class",
};

export function hyperliquidCsvFieldLabel(field: string, fallback: string): string {
  return HYPERLIQUID_CSV_FIELD_LABELS[field] ?? fallback;
}

function normalizeAssetClasses(classes: string[]): string[] {
  return classes.map((c) => c.trim().toLowerCase()).filter(Boolean);
}

/** Query params for POST /broker/hyperliquid/csv-import (no broker_name). */
export function buildHyperliquidCsvImportParams(
  values: Record<string, string>,
  assetClasses: string[] = []
): Record<string, string | string[]> {
  const accountName = (values.account_name || values.accountName || "").trim();
  const timeZone = (values.time_zone || values.timeZone || "").trim();
  const params: Record<string, string | string[]> = {
    account_name: accountName,
    time_zone: timeZone,
  };

  const fromPicker = normalizeAssetClasses(assetClasses);
  const single = (values.traded_asset_classes || values.tradedAssetClasses || "").trim();
  if (fromPicker.length > 0) {
    params.traded_asset_classes = fromPicker;
  } else if (single) {
    params.traded_asset_classes = [single.toLowerCase()];
  }

  return params;
}

/** POST /broker/credentials — broker_name is routing only, not user-facing. */
export function buildHyperliquidConnectCredentialPayload(
  values: Record<string, string>
): Record<string, unknown> {
  return {
    broker_name: HYPERLIQUID_BROKER_SLUG,
    account_name: (values.account_name || values.accountName || "").trim(),
  };
}
